import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine.ts';
import { type GridPosition } from '../../../machine/state/types.ts';
import { hasActiveEdit } from '../../../machine/utils/has-active-edit.ts';
import { isSameCell } from '../../../machine/utils/is-same-cell.ts';
import {
  type KeyboardNavigationActionFamily,
  type SelectionUiInteractionHandler
} from '../../selection-ui-interaction.ts';
import { type TableNavigationAdapter } from '../table-navigation-adapter.ts';
import { blurCheckboxTarget } from '../utils/blur-checkbox-target.ts';
import { matchesPrimaryModifier } from '../utils/match-primary-modifier.ts';

interface GridBounds {
  entryCount: number;
  fieldCount: number;
}

/**
 * Moves one cell in the requested direction while staying inside the visible
 * table bounds.
 */
function moveOneCell(
  focusedCell: GridPosition,
  direction: 'up' | 'down' | 'left' | 'right',
  bounds: GridBounds
): GridPosition {
  const { row, col } = focusedCell;

  switch (direction) {
    case 'up':
      return { row: { index: Math.max(0, row.index - 1) }, col };
    case 'down':
      return {
        row: { index: Math.min(bounds.entryCount - 1, row.index + 1) },
        col
      };
    case 'left':
      return { row, col: { index: Math.max(0, col.index - 1) } };
    case 'right':
      return {
        row,
        col: { index: Math.min(bounds.fieldCount - 1, col.index + 1) }
      };
  }
}

/**
 * Moves to the current row/column edge for Cmd/Ctrl+Arrow behavior.
 */
function edgeCell(
  focusedCell: GridPosition,
  direction: 'up' | 'down' | 'left' | 'right',
  bounds: GridBounds
): GridPosition {
  const { row, col } = focusedCell;

  switch (direction) {
    case 'up':
      return { row: { index: 0 }, col };
    case 'down':
      return { row: { index: bounds.entryCount - 1 }, col };
    case 'left':
      return { row, col: { index: 0 } };
    case 'right':
      return { row, col: { index: bounds.fieldCount - 1 } };
  }
}

/**
 * `capture.properties.key` is the ShortcutKeyMap-mapped value, NOT the raw
 * DOM `event.key`. Arrow keys are mapped by `ShortcutKeyMap.fromDOMKey`:
 *
 *   ArrowUp → ↑, ArrowDown → ↓, ArrowLeft → ←, ArrowRight → →
 *
 * @see packages/shortcuts/src/shortcuts-keymap.ts
 */
const ARROW_TO_DIRECTION: Record<string, 'up' | 'down' | 'left' | 'right'> = {
  '↑': 'up',
  '↓': 'down',
  '←': 'left',
  '→': 'right'
};

/**
 * Handles table grid navigation keys once a cell already has selection focus.
 *
 * This intentionally ignores active edit mode so the editor keeps normal text
 * cursor behavior. Plain arrows move the focused cell, Shift extends the active
 * range from the lead cell, and Cmd/Ctrl jumps to table edges.
 */
export function createTableNavigationKeyDownHandler(
  adapter: TableNavigationAdapter,
  onSelectionUiInteraction?: SelectionUiInteractionHandler
) {
  return function handleTableNavigationKeyDown(
    capture: KeyboardDownCapture,
    machine: SelectionsMachine
  ): void {
    const state = machine.getState();

    if (hasActiveEdit(state) || !state.focusedCell || state.mode === 'idle') {
      return;
    }

    const { key, event: domEvent } = capture.properties;
    const primaryMod = matchesPrimaryModifier(domEvent);
    const entryCount = adapter.getVisibleEntryCount();
    const fieldCount = adapter.getVisibleFieldIds().length;
    const bounds = { entryCount, fieldCount };

    if (entryCount === 0 || fieldCount === 0) {
      return;
    }

    const direction = ARROW_TO_DIRECTION[key];

    if (!direction) {
      return;
    }

    domEvent.preventDefault();

    const activeSelection = state.selections[state.selections.length - 1];
    const origin =
      domEvent.shiftKey && activeSelection ? activeSelection.lead : state.focusedCell;

    const target = primaryMod
      ? edgeCell(origin, direction, bounds)
      : moveOneCell(origin, direction, bounds);
    const shouldExtendSelection =
      domEvent.shiftKey &&
      (!activeSelection || !isSameCell(activeSelection.lead, target));

    if (shouldExtendSelection) {
      const actionFamily: KeyboardNavigationActionFamily = primaryMod
        ? 'extendRangeToEdge'
        : 'extendRange';
      const startTime = onSelectionUiInteraction ? performance.now() : 0;

      machine.dispatch({
        type: 'extendToCell',
        cell: target,
        source: 'keyboard'
      });
      onSelectionUiInteraction?.({
        source: 'keyboard',
        startTime,
        detail: { actionFamily, target }
      });
    }

    if (domEvent.shiftKey) {
      capture.properties.consumed = true;

      return;
    }

    const shouldMoveFocus = !isSameCell(state.focusedCell, target);

    if (shouldMoveFocus) {
      const actionFamily: KeyboardNavigationActionFamily = primaryMod
        ? 'jumpToEdge'
        : 'moveCell';
      const startTime = onSelectionUiInteraction ? performance.now() : 0;

      blurCheckboxTarget(domEvent.target);
      machine.dispatch({
        type: 'selectCell',
        cell: target,
        source: 'keyboard'
      });
      onSelectionUiInteraction?.({
        source: 'keyboard',
        startTime,
        detail: { actionFamily, target }
      });
    }

    capture.properties.consumed = true;
  };
}
